/**
 *
 * DocumentSplitterInvoice
 *
 */

import React, { forwardRef, memo, useRef, useState, useEffect } from 'react';
import { VariableSizeList as List, ListChildComponentProps, areEqual } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import clsx from 'clsx';
import { ListColumnData } from './DocumentSplitter';

// api
import { InvoiceForSplittingWithSrc } from 'lib/api/invoiceContainer';
import { generateImageURL } from 'lib/api/images';

// icons
import { ZoomInDocument, RemoveDocument, UndoIcon, UnfoldMoreIcon } from 'lib/domonda-icons';

// ui
import { Flex, Portal, IconButton } from '@domonda/ui';

// utils
import { getItemSize, getEstimatedItemSize } from './utils';

// decorate
import { decorate, Decorate } from './decorate';

interface ListInnerProps {
  children: React.ReactNode;
  style: React.CSSProperties;
}

interface ListRowData extends Pick<ListColumnData, 'classes' | 'doDeletePage' | 'doSplitAfter'> {
  pagePreview: number | null;
  setPagePreview: React.Dispatch<React.SetStateAction<number | null>>;
  invoice: InvoiceForSplittingWithSrc;
  invoiceIndex: number;
}

interface ListRowProps extends ListChildComponentProps {
  data: ListRowData;
}

export interface DocumentSplitterInvoiceProps {
  style: React.CSSProperties;
  zoom: number;
  invoice: InvoiceForSplittingWithSrc;
  invoiceIndex: number;
  doDeletePage: (invoiceIndex: number, pageIndex: number) => void;
  doSplitAfter: (invoiceIndex: number, pageIndex: number) => void;
}

const LIST_PADDING = 15;

const ListInner = forwardRef<HTMLDivElement, ListInnerProps>(function ListInner(
  { style, ...rest },
  ref,
) {
  return <div ref={ref} style={{ ...style, position: 'relative' }} {...rest} />;
});

if (process.env.NODE_ENV !== 'production') ListInner.displayName = 'ListInner';

const ListRow = memo<ListRowProps>(({ data, index, style }) => {
  const {
    classes,
    pagePreview,
    setPagePreview,
    invoice,
    invoiceIndex,
    doDeletePage,
    doSplitAfter,
  } = data;
  const pageIndex = Math.floor(index / 2);
  const page = invoice.Pages[pageIndex];

  if (index % 2) {
    return (
      <Flex container justify="center" style={style}>
        <Flex item className={classes.invoiceSplit}>
          <IconButton color="primary" onClick={() => doSplitAfter(invoiceIndex, pageIndex)}>
            <UnfoldMoreIcon />
          </IconButton>
        </Flex>
      </Flex>
    );
  } else {
    return (
      <Flex container direction="column" style={style}>
        <Flex
          item
          flex={1}
          className={clsx(classes.invoicePage, page.Deleted && classes.invoicePageDeleted)}
        >
          <IconButton
            className={clsx(classes.invoicePageButton, classes.invoicePagePreviewButton)}
            onClick={() => setPagePreview(pageIndex)}
          >
            <ZoomInDocument />
          </IconButton>

          <IconButton
            className={clsx(classes.invoicePageButton, classes.invoicePageDeleteButton)}
            onClick={() => doDeletePage(invoiceIndex, pageIndex)}
          >
            {page.Deleted ? <UndoIcon /> : <RemoveDocument />}
          </IconButton>

          <img src={generateImageURL(page.Src)} width="100%" />
        </Flex>

        {pagePreview === pageIndex && (
          <Portal>
            <div className={classes.invoicePagePreview} onClick={() => setPagePreview(null)}>
              <div className={classes.invoicePagePreviewBackdrop} />
              <img src={generateImageURL(page.Src)} width="80%" />
            </div>
          </Portal>
        )}
      </Flex>
    );
  }
}, areEqual);

if (process.env.NODE_ENV !== 'production') ListRow.displayName = 'ListRow';

const DocumentSplitterInvoice: React.FC<DocumentSplitterInvoiceProps & Decorate> = (props) => {
  const { classes, style, zoom, invoice, invoiceIndex, doDeletePage, doSplitAfter } = props;

  const listRef = useRef<List | null>(null);
  const [pagePreview, setPagePreview] = useState<number | null>(null);

  useEffect(() => {
    // Must trigger re-render of the list
    // to make it recalculate item sizes after zoom value changes.
    if (listRef.current) {
      (listRef.current as any)._instanceProps.estimatedItemSize = getEstimatedItemSize(
        'vertical',
        zoom,
      );
      listRef.current.resetAfterIndex(0, true);
    }
  }, [zoom]);

  const itemData: ListRowData = {
    classes,
    pagePreview,
    setPagePreview,
    invoice,
    invoiceIndex,
    doDeletePage,
    doSplitAfter,
  };

  return (
    <Flex container style={style}>
      <Flex item flex={1} className={classes.invoice}>
        <AutoSizer>
          {({ width, height }) => (
            <List
              ref={listRef}
              width={width - 4}
              height={height - 4}
              innerElementType={ListInner}
              itemData={itemData}
              itemSize={(index) => getItemSize('vertical', index, zoom)}
              estimatedItemSize={getEstimatedItemSize('vertical', zoom)}
              itemCount={invoice.Pages.length * 2 - 1} // last item doesn't have a button
              style={{ padding: LIST_PADDING }}
            >
              {ListRow}
            </List>
          )}
        </AutoSizer>
      </Flex>
    </Flex>
  );
};

const ComposedDocumentSplitterInvoice = decorate(DocumentSplitterInvoice);
export { ComposedDocumentSplitterInvoice as DocumentSplitterInvoice };
